import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Badge } from '../ui/badge';
import { Switch } from '../ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '../ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { toast } from 'sonner';
import { axiosInstance } from '../../App';
import {
  Mail,
  MessageSquare,
  Edit,
  Eye,
  FileText,
  Send,
  CheckCircle,
  XCircle,
  Clock,
  RefreshCw,
  Settings,
  Palette,
  History,
  Zap,
  Plus,
  Trash2,
  Download,
  AlertCircle
} from 'lucide-react';
import { formatDate, formatDateTime, formatTime } from '../../utils/dateUtils';

const EVENT_TYPES = [
  { value: 'quote_submitted', label: 'Quote Submitted', icon: FileText, color: 'bg-blue-100 text-blue-800' },
  { value: 'quote_approved', label: 'Quote Approved', icon: CheckCircle, color: 'bg-green-100 text-green-800' },
  { value: 'job_status_updated', label: 'Job Status Updated', icon: Zap, color: 'bg-purple-100 text-purple-800' },
  { value: 'invoice_raised', label: 'Invoice Raised', icon: FileText, color: 'bg-orange-100 text-orange-800' },
  { value: 'invoice_paid', label: 'Invoice Paid', icon: CheckCircle, color: 'bg-emerald-100 text-emerald-800' }
];

const AVAILABLE_VARIABLES = [
  '{{CustomerName}}', '{{ServiceType}}', '{{JobID}}', '{{QuoteID}}', '{{QuoteAmount}}',
  '{{InvoiceID}}', '{{InvoiceAmount}}', '{{HandymanName}}', '{{JobStatus}}',
  '{{PaymentStatus}}', '{{JobPhotosLink}}', '{{CompanyName}}'
];

export default function NotificationSettings() {
  const [templates, setTemplates] = useState([]);
  const [logs, setLogs] = useState([]);
  const [logsStats, setLogsStats] = useState(null);
  const [settings, setSettings] = useState({});
  const [loading, setLoading] = useState(true);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [templateDialogOpen, setTemplateDialogOpen] = useState(false);
  const [previewDialogOpen, setPreviewDialogOpen] = useState(false);
  const [previewContent, setPreviewContent] = useState(null);
  const [settingsDialogOpen, setSettingsDialogOpen] = useState(false);

  const [templateForm, setTemplateForm] = useState({
    name: '',
    event_type: '',
    subject: '',
    email_body: '',
    whatsapp_body: '',
    channels: 'email',
    is_active: true
  });

  const [settingsForm, setSettingsForm] = useState({
    company_name: '',
    logo_url: '',
    primary_color: '#0d9488',
    secondary_color: '#1e40af',
    footer_text: '',
    contact_email: '',
    contact_phone: '',
    website_url: ''
  });

  const fetchData = async () => {
    try {
      const [templatesRes, logsRes, statsRes, settingsRes] = await Promise.all([
        axiosInstance.get('/notification/templates'),
        axiosInstance.get('/notification/logs?limit=50'),
        axiosInstance.get('/notification/logs/stats'),
        axiosInstance.get('/notification/settings')
      ]);
      setTemplates(templatesRes.data);
      setLogs(logsRes.data);
      setLogsStats(statsRes.data);
      setSettings(settingsRes.data);
      setSettingsForm(settingsRes.data);
    } catch (error) {
      toast.error('Failed to load notification data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const resetTemplateForm = () => {
    setTemplateForm({
      name: '',
      event_type: '',
      subject: '',
      email_body: '',
      whatsapp_body: '',
      channels: 'email',
      is_active: true
    });
    setEditingTemplate(null);
  };

  const handleSaveTemplate = async () => {
    if (!templateForm.name || !templateForm.event_type || !templateForm.subject) {
      toast.error('Name, event type, and subject are required');
      return;
    }

    try {
      if (editingTemplate) {
        await axiosInstance.patch(`/notification/templates/${editingTemplate.id}`, templateForm);
        toast.success('Template updated');
      } else {
        await axiosInstance.post('/notification/templates', templateForm);
        toast.success('Template created');
      }
      setTemplateDialogOpen(false);
      resetTemplateForm();
      fetchData();
    } catch (error) {
      toast.error('Failed to save template');
    }
  };

  const handlePreview = async (template) => {
    try {
      const response = await axiosInstance.post('/notification/templates/preview', {
        template_id: template.id
      });
      setPreviewContent(response.data);
      setPreviewDialogOpen(true);
    } catch (error) {
      toast.error('Failed to preview template');
    }
  };

  const handleToggleActive = async (template) => {
    try {
      await axiosInstance.patch(`/notification/templates/${template.id}`, {
        is_active: !template.is_active
      });
      toast.success(`Template ${template.is_active ? 'disabled' : 'enabled'}`);
      fetchData();
    } catch (error) {
      toast.error('Failed to update template');
    }
  };

  const openEditDialog = (template) => {
    setEditingTemplate(template);
    setTemplateForm({
      name: template.name,
      event_type: template.event_type,
      subject: template.subject,
      email_body: template.email_body || '',
      whatsapp_body: template.whatsapp_body || '',
      channels: template.channels || 'email',
      is_active: template.is_active
    });
    setTemplateDialogOpen(true);
  };

  const handleSaveSettings = async () => {
    try {
      await axiosInstance.patch('/notification/settings', settingsForm);
      toast.success('Settings saved');
      setSettingsDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error('Failed to save settings');
    }
  };

  const handleDeleteTemplate = async (template) => {
    if (!window.confirm(`Are you sure you want to delete "${template.name}"?`)) return;
    
    try {
      await axiosInstance.delete(`/notification/templates/${template.id}`);
      toast.success('Template deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete template');
    }
  };

  const handleSeedTemplates = async () => {
    try {
      const response = await axiosInstance.post('/notification/seed-templates');
      toast.success(`Created ${response.data.templates_created} default templates`);
      fetchData();
    } catch (error) {
      toast.error('Failed to seed templates');
    }
  };

  const handleTestSend = async (template) => {
    const testEmail = window.prompt('Enter email address to send test notification:');
    if (!testEmail) return;

    try {
      await axiosInstance.post('/notification/test-send', {
        template_id: template.id,
        test_email: testEmail
      });
      toast.success(`Test notification sent to ${testEmail}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send test notification');
    }
  };

  const insertVariable = (variable) => {
    setTemplateForm(prev => ({
      ...prev,
      email_body: prev.email_body + variable
    }));
  };

  const getEventInfo = (eventType) => {
    return EVENT_TYPES.find(e => e.value === eventType) || { label: eventType, color: 'bg-gray-100 text-gray-800' };
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return formatDateTime(dateStr);
  };

  return (
    <div className="p-4 md:p-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Notification Engine</h1>
          <p className="text-gray-500">Manage email and WhatsApp notifications</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button onClick={() => { resetTemplateForm(); setTemplateDialogOpen(true); }} className="bg-[#595959] hover:bg-[#404040]">
            <Plus className="w-4 h-4 mr-2" />
            New Template
          </Button>
          <Button variant="outline" onClick={() => setSettingsDialogOpen(true)}>
            <Palette className="w-4 h-4 mr-2" />
            Branding
          </Button>
          <Button variant="outline" onClick={fetchData}>
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      {logsStats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center">
                  <Send className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{logsStats.total}</p>
                  <p className="text-sm text-gray-500">Total Sent</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-green-50 flex items-center justify-center">
                  <CheckCircle className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{logsStats.success_rate}%</p>
                  <p className="text-sm text-gray-500">Success Rate</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-purple-50 flex items-center justify-center">
                  <Mail className="w-5 h-5 text-purple-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{logsStats.by_channel?.email || 0}</p>
                  <p className="text-sm text-gray-500">Emails</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-green-50 flex items-center justify-center">
                  <MessageSquare className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{logsStats.by_channel?.whatsapp || 0}</p>
                  <p className="text-sm text-gray-500">WhatsApp</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Tabs */}
      <Tabs defaultValue="templates" className="space-y-4">
        <TabsList>
          <TabsTrigger value="templates" className="flex items-center gap-2">
            <FileText className="w-4 h-4" />
            Templates
          </TabsTrigger>
          <TabsTrigger value="logs" className="flex items-center gap-2">
            <History className="w-4 h-4" />
            Logs
          </TabsTrigger>
        </TabsList>

        {/* Templates Tab */}
        <TabsContent value="templates">
          <Card>
            <CardHeader>
              <CardTitle>Email Templates</CardTitle>
              <CardDescription>Configure notification templates for each event</CardDescription>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="text-center py-8 text-gray-500">Loading...</div>
              ) : templates.length === 0 ? (
                <div className="text-center py-12">
                  <FileText className="w-12 h-12 mx-auto text-gray-300 mb-4" />
                  <p className="text-gray-500 mb-4">No templates configured</p>
                  <Button onClick={handleSeedTemplates} className="bg-[#595959] hover:bg-[#404040]">
                    <Download className="w-4 h-4 mr-2" />
                    Load Default Templates
                  </Button>
                </div>
              ) : (
                <div className="space-y-4">
                  {templates.map(template => {
                    const eventInfo = getEventInfo(template.event_type);
                    return (
                      <Card key={template.id} className={`border ${!template.is_active ? 'opacity-60' : ''}`}>
                        <CardContent className="p-4">
                          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                            <div className="flex items-start gap-4">
                              <div className="w-12 h-12 rounded-lg bg-gray-100 flex items-center justify-center flex-shrink-0">
                                <Mail className="w-6 h-6 text-[#595959]" />
                              </div>
                              <div>
                                <div className="flex items-center gap-2 flex-wrap">
                                  <h3 className="font-semibold text-gray-900">{template.name}</h3>
                                  <Badge className={eventInfo.color}>{eventInfo.label}</Badge>
                                  {!template.is_active && (
                                    <Badge variant="outline" className="text-gray-500">Disabled</Badge>
                                  )}
                                </div>
                                <p className="text-sm text-gray-500 mt-1">{template.subject}</p>
                                <div className="flex items-center gap-2 mt-2">
                                  {(template.channels === 'email' || template.channels === 'both') && (
                                    <Badge variant="outline" className="text-xs">
                                      <Mail className="w-3 h-3 mr-1" /> Email
                                    </Badge>
                                  )}
                                  {(template.channels === 'whatsapp' || template.channels === 'both') && (
                                    <Badge variant="outline" className="text-xs">
                                      <MessageSquare className="w-3 h-3 mr-1" /> WhatsApp
                                    </Badge>
                                  )}
                                </div>
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              <Button variant="outline" size="sm" onClick={() => handlePreview(template)} title="Preview">
                                <Eye className="w-4 h-4" />
                              </Button>
                              <Button variant="outline" size="sm" onClick={() => handleTestSend(template)} title="Send Test">
                                <Send className="w-4 h-4" />
                              </Button>
                              <Button variant="outline" size="sm" onClick={() => openEditDialog(template)} title="Edit">
                                <Edit className="w-4 h-4" />
                              </Button>
                              <Button variant="outline" size="sm" onClick={() => handleDeleteTemplate(template)} className="text-red-600 hover:text-red-700 hover:bg-red-50" title="Delete">
                                <Trash2 className="w-4 h-4" />
                              </Button>
                              <Switch
                                checked={template.is_active}
                                onCheckedChange={() => handleToggleActive(template)}
                              />
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Logs Tab */}
        <TabsContent value="logs">
          <Card>
            <CardHeader>
              <CardTitle>Notification Logs</CardTitle>
              <CardDescription>History of all sent notifications</CardDescription>
            </CardHeader>
            <CardContent>
              {logs.length === 0 ? (
                <div className="text-center py-12">
                  <History className="w-12 h-12 mx-auto text-gray-300 mb-4" />
                  <p className="text-gray-500">No notifications sent yet</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {logs.map(log => {
                    const eventInfo = getEventInfo(log.event_type);
                    return (
                      <div key={log.id} className="flex items-start gap-4 p-3 border rounded-lg">
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                          log.status === 'sent' ? 'bg-green-100' : 'bg-red-100'
                        }`}>
                          {log.channel === 'email' ? (
                            <Mail className={`w-5 h-5 ${log.status === 'sent' ? 'text-green-600' : 'text-red-600'}`} />
                          ) : (
                            <MessageSquare className={`w-5 h-5 ${log.status === 'sent' ? 'text-green-600' : 'text-red-600'}`} />
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <Badge className={eventInfo.color}>{eventInfo.label}</Badge>
                            <Badge variant={log.status === 'sent' ? 'default' : 'destructive'}>
                              {log.status}
                            </Badge>
                          </div>
                          <p className="text-sm font-medium mt-1 truncate">
                            To: {log.recipient_email || log.recipient_phone}
                          </p>
                          {log.subject && (
                            <p className="text-sm text-gray-500 truncate">{log.subject}</p>
                          )}
                          {log.error_message && (
                            <p className="text-xs text-red-500 mt-1">{log.error_message}</p>
                          )}
                        </div>
                        <div className="text-xs text-gray-400 whitespace-nowrap">
                          {formatDate(log.sent_at)}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Edit Template Dialog */}
      <Dialog open={templateDialogOpen} onOpenChange={(open) => { setTemplateDialogOpen(open); if (!open) resetTemplateForm(); }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingTemplate ? 'Edit Template' : 'Create Template'}</DialogTitle>
            <DialogDescription>Configure email and WhatsApp notification content</DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Template Name *</Label>
                <Input
                  value={templateForm.name}
                  onChange={(e) => setTemplateForm({ ...templateForm, name: e.target.value })}
                  placeholder="e.g., Quote Notification"
                />
              </div>
              <div className="space-y-2">
                <Label>Event Type *</Label>
                <Select
                  value={templateForm.event_type}
                  onValueChange={(v) => setTemplateForm({ ...templateForm, event_type: v })}
                  disabled={!!editingTemplate}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select event" />
                  </SelectTrigger>
                  <SelectContent>
                    {EVENT_TYPES.map(e => (
                      <SelectItem key={e.value} value={e.value}>{e.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Channels</Label>
                <Select
                  value={templateForm.channels}
                  onValueChange={(v) => setTemplateForm({ ...templateForm, channels: v })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="email">Email Only</SelectItem>
                    <SelectItem value="whatsapp">WhatsApp Only</SelectItem>
                    <SelectItem value="both">Both Email & WhatsApp</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-center gap-2 pt-6">
                <Switch
                  checked={templateForm.is_active}
                  onCheckedChange={(checked) => setTemplateForm({ ...templateForm, is_active: checked })}
                />
                <Label>Active</Label>
              </div>
            </div>

            <div className="space-y-2">
              <Label>Email Subject *</Label>
              <Input
                value={templateForm.subject}
                onChange={(e) => setTemplateForm({ ...templateForm, subject: e.target.value })}
                placeholder="e.g., Your Quote Request #{{QuoteID}}"
              />
            </div>

            <div className="space-y-2">
              <Label>Email Body (HTML) *</Label>
              <Textarea
                value={templateForm.email_body}
                onChange={(e) => setTemplateForm({ ...templateForm, email_body: e.target.value })}
                rows={8}
                placeholder="<h2>Hello {{CustomerName}},</h2><p>Your quote is ready...</p>"
              />
            </div>

            <div className="space-y-2">
              <Label className="text-sm text-gray-500">Available Variables (click to insert)</Label>
              <div className="flex flex-wrap gap-1">
                {AVAILABLE_VARIABLES.map(v => (
                  <Button
                    key={v}
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => insertVariable(v)}
                    className="text-xs"
                  >
                    {v}
                  </Button>
                ))}
              </div>
            </div>

            {(templateForm.channels === 'whatsapp' || templateForm.channels === 'both') && (
              <div className="space-y-2">
                <Label>WhatsApp Message</Label>
                <Textarea
                  value={templateForm.whatsapp_body}
                  onChange={(e) => setTemplateForm({ ...templateForm, whatsapp_body: e.target.value })}
                  rows={4}
                  placeholder="Hi {{CustomerName}}! Your quote is ready..."
                />
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setTemplateDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleSaveTemplate} className="bg-[#595959] hover:bg-[#404040]">
              {editingTemplate ? 'Update' : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Preview Dialog */}
      <Dialog open={previewDialogOpen} onOpenChange={setPreviewDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Template Preview</DialogTitle>
            <DialogDescription>Preview with sample data</DialogDescription>
          </DialogHeader>
          {previewContent && (
            <div className="space-y-4">
              <div>
                <Label className="text-xs text-gray-500">Subject</Label>
                <p className="font-medium">{previewContent.subject}</p>
              </div>
              <div>
                <Label className="text-xs text-gray-500">Email Preview</Label>
                <div 
                  className="border rounded-lg p-4 mt-1 bg-white max-h-96 overflow-y-auto"
                  dangerouslySetInnerHTML={{ __html: previewContent.email_html }}
                />
              </div>
              {previewContent.whatsapp_body && (
                <div>
                  <Label className="text-xs text-gray-500">WhatsApp Preview</Label>
                  <div className="bg-green-50 rounded-lg p-4 mt-1 whitespace-pre-wrap">
                    {previewContent.whatsapp_body}
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Branding Settings Dialog */}
      <Dialog open={settingsDialogOpen} onOpenChange={setSettingsDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Branding Settings</DialogTitle>
            <DialogDescription>Customize email appearance</DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Company Name</Label>
              <Input
                value={settingsForm.company_name}
                onChange={(e) => setSettingsForm({ ...settingsForm, company_name: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label>Logo URL</Label>
              <Input
                value={settingsForm.logo_url || ''}
                onChange={(e) => setSettingsForm({ ...settingsForm, logo_url: e.target.value })}
                placeholder="https://..."
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Primary Color</Label>
                <div className="flex gap-2">
                  <input
                    type="color"
                    value={settingsForm.primary_color}
                    onChange={(e) => setSettingsForm({ ...settingsForm, primary_color: e.target.value })}
                    className="w-10 h-10 rounded cursor-pointer"
                  />
                  <Input
                    value={settingsForm.primary_color}
                    onChange={(e) => setSettingsForm({ ...settingsForm, primary_color: e.target.value })}
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Secondary Color</Label>
                <div className="flex gap-2">
                  <input
                    type="color"
                    value={settingsForm.secondary_color}
                    onChange={(e) => setSettingsForm({ ...settingsForm, secondary_color: e.target.value })}
                    className="w-10 h-10 rounded cursor-pointer"
                  />
                  <Input
                    value={settingsForm.secondary_color}
                    onChange={(e) => setSettingsForm({ ...settingsForm, secondary_color: e.target.value })}
                  />
                </div>
              </div>
            </div>
            <div className="space-y-2">
              <Label>Footer Text</Label>
              <Input
                value={settingsForm.footer_text}
                onChange={(e) => setSettingsForm({ ...settingsForm, footer_text: e.target.value })}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Contact Email</Label>
                <Input
                  value={settingsForm.contact_email}
                  onChange={(e) => setSettingsForm({ ...settingsForm, contact_email: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>Contact Phone</Label>
                <Input
                  value={settingsForm.contact_phone || ''}
                  onChange={(e) => setSettingsForm({ ...settingsForm, contact_phone: e.target.value })}
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Website URL</Label>
              <Input
                value={settingsForm.website_url || ''}
                onChange={(e) => setSettingsForm({ ...settingsForm, website_url: e.target.value })}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setSettingsDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleSaveSettings} className="bg-[#595959] hover:bg-[#404040]">
              Save Settings
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
